import type { FC, ReactNode } from 'react'
import type { Testimonial } from '@/types'

import Link from 'next/link'
import { GraduationCap, Quote } from 'lucide-react'
import { cn } from '@/lib/utils'
import { courseHref } from '@/lib/routes'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'

interface TestimonialsSectionProps {
    testimonials: Array<Testimonial>
}

const initials = (name: string): string => name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('')

export const TestimonialsSection: FC<TestimonialsSectionProps> = ({ testimonials }): ReactNode => {
    if (testimonials.length === 0) return null

    return (
        <section id='testimonials' aria-labelledby='testimonials-title' className='py-20 sm:py-28'>
            <Container>
                <SectionHeading
                    id='testimonials-title'
                    align='center'
                    eyebrow="Student stories"
                    title="Real progress, in their own words"
                    description="Office workers, retirees, students and career changers. Here is what they say after finishing a course with us."
                />

                <div className='mt-14 grid gap-6 md:grid-cols-2'>
                    {testimonials.map((testimonial, index) => {
                        const dark = index % 3 === 0

                        return (
                            <figure
                                key={testimonial.id}
                                className={cn(
                                    'flex flex-col rounded-3xl border p-7 sm:p-9',
                                    dark ? 'border-transparent bg-foreground text-background' : 'bg-card text-card-foreground'
                                )}
                            >
                                <Quote className={cn('size-8', dark ? 'text-accent' : 'text-primary')} aria-hidden='true' />

                                <blockquote className='mt-5 flex-1'>
                                    <p className='text-lg leading-relaxed sm:text-xl' data-mdlfy-cms={`testimonials:${testimonial.id}:quote`}>
                                        {testimonial.quote}
                                    </p>
                                </blockquote>

                                <figcaption className={cn('mt-8 flex flex-wrap items-center justify-between gap-4 border-t pt-5', dark ? 'border-background/15' : 'border-border')}>
                                    <div className='flex items-center gap-3'>
                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'flex size-11 shrink-0 items-center justify-center rounded-full font-heading text-sm font-semibold',
                                                dark ? 'bg-accent text-accent-foreground' : 'bg-secondary text-secondary-foreground'
                                            )}
                                        >
                                            {initials(testimonial.name)}
                                        </span>

                                        <div>
                                            <p className='font-semibold' data-mdlfy-cms={`testimonials:${testimonial.id}:name`}>
                                                {testimonial.name}
                                            </p>

                                            {testimonial.role && (
                                                <p className={cn('text-sm', dark ? 'text-background/65' : 'text-muted-foreground')} data-mdlfy-cms={`testimonials:${testimonial.id}:role`}>
                                                    {testimonial.role}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {testimonial.course && (
                                        <Link
                                            href={courseHref(testimonial.course.slug)}
                                            className={cn(
                                                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-ring',
                                                dark ? 'border-background/20 hover:border-accent hover:text-accent' : 'hover:border-primary hover:text-primary'
                                            )}
                                        >
                                            <GraduationCap className='size-3.5' aria-hidden='true' />
                                            <span data-mdlfy-cms={`courses:${testimonial.course.id}:title`}>{testimonial.course.title}</span>
                                        </Link>
                                    )}
                                </figcaption>
                            </figure>
                        )
                    })}
                </div>
            </Container>
        </section>
    )
}
